/**
 * Redux-style action creators for the learner app store.
 */
import asterAxios from "utils/api";
import { isNetworkError, isOnline } from "offline/network";
import { putGqlCache } from "offline/cache";
import { clearSyncQueue } from "offline/queue";
import { postGql, postGqlKeepalive } from "offline/gqlClient";
import { markFirstLoadPrimed, parseAppPath } from "offline/firstLoad";
import { readLastPath, setPreferSession } from "offline/session";
import { convert2String } from "utils/string";
import toaster from "utils/toast.msg";
import { asArray, asList, asObject, safeParse } from "utils/safeData";
import { clearSessionAndRedirect, isAuthFailure } from "utils/authErrors";
import {
  M_ADD_CHAT,
  M_CHAT_TYPING,
  M_PRESENCE_LEAVE,
  M_PRESENCE_PING,
  M_CERTIFICATE_CREATE,
  M_COMMENT_CREATE,
  M_COMMENT_EDIT,
  M_MODULE_INSPECT_EDIT,
  M_REPLAY_INSPECT_EDIT,
  M_REVIEW_EDIT,
  M_STUDENT_ANSWER_EDIT,
  M_USER_EDIT,
  M_VIMEO_INSPECT_EDIT,
  Q_APP_COURSE_DETAIL_GET,
  Q_APP_COURSE_UNIT_GET,
  Q_APP_DASHBOARD_GET,
  Q_APP_FIRST_LOAD_GET,
  Q_COMMENT_LIST,
  Q_LIST_CHAT,
  Q_NETWORK_LIST,
  Q_LIST_USER_CHART,
  Q_LOGIN_HISTORY_LIST,
  Q_MODULE_INSPECT_LIST,
  Q_PAST_SESSION_LIST,
  Q_REPLAY_INSPECT_LIST,
  Q_SEND_COACH_EMAIL,
  Q_SEND_SURVEY_EMAIL,
  Q_GOOGLE_INSPECT_LIST,
} from "./graphql";

/**
 * Sets whether the side navigation is collapsed.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {boolean} value Mini sidenav flag.
 * @returns {void}
 */
export const actionMiniSidenav = (dispatch, value) => dispatch({ type: "SET_MINI_SIDENAV", value });

/**
 * Sets whether the header side navigation is open.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {boolean} value Header sidenav flag.
 * @returns {void}
 */
export const actionheaderSidenav = (dispatch, value) => dispatch({ type: "SET_HEADER_SIDENAV", value });

/**
 * Sets the mobile layout breakpoint flag.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {boolean} value Mobile flag.
 * @returns {void}
 */
export const actionMobile = (dispatch, value) => dispatch({ type: "SET_MOBILE", value });

/**
 * Sets dark mode.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {boolean} value Dark mode flag.
 * @returns {void}
 */
export const actionDarkMode = (dispatch, value) => dispatch({ type: "SET_DARK_MODE", value });

/**
 * Applies a parsed dashboard payload to the app store.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {object} res Dashboard JSON.
 * @returns {void}
 */
const applyDashboardPayload = (dispatch, res) => {
  dispatch({ type: "SET_USER", value: asObject(res.user_get) });
  dispatch({ type: "SET_COURSE_LIST", value: asList(res.course_list) });
  dispatch({ type: "SET_ROUTER_TRACK", value: asArray(res.router_track_get) });
  dispatch({ type: "SET_CATEGORY_LIST", value: asList(res.category_list) });
  dispatch({ type: "SET_TERM", value: asObject(res.term_get) });
  dispatch({ type: "SET_LOGIN_HIST_LIST", value: asList(res.user_login_log_list) });
  dispatch({ type: "SET_IS_PROFILE_ALERT", value: asList(res.user_login_log_list).total < 3 });
  dispatch({ type: "SET_CERTIFICATE_LIST", value: asList(res.certificate_list) });
  dispatch({ type: "SET_CERT_VAR", value: asObject(res.certificate_variable_get) });
  dispatch({ type: "SET_THEME_LIST", value: asArray(res.theme_list) });
  dispatch({ type: "SET_ADMIN", value: asObject(res.admin_info) });
  dispatch({ type: "SET_CHAT_ENABLED", value: res.chat_enabled == null ? true : res.chat_enabled });
  dispatch({ type: "SET_COMMENT_COUNT", value: res.comment_count || 0 });
  dispatch({ type: "SET_PREFERENCE", value: asObject(res.preference_get) });
  dispatch({ type: "SET_USER_GROUP_LIST", value: asArray(res.user_groups) });
  dispatch({ type: "SET_NOTIFICATION_LIST", value: asArray(res.notification_list) });
  dispatch({ type: "SET_MEET_EVENT_LIST", value: asList(res.meet_event_list) });
  setPreferSession(false);
};

/**
 * Applies a parsed course-detail payload to the app store.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {object} res Course detail JSON.
 * @returns {void}
 */
const applyCourseDetailPayload = (dispatch, res) => {
  dispatch({ type: "SET_COURSE_STRUCTURE", value: asObject(res.course_structure_get) });
  dispatch({ type: "SET_COMMENT_LIST", value: asList(res.comment_list) });
  dispatch({ type: "SET_UPCOMING_EVENT_LIST", value: asList(res.meeting_upcoming_list) });
  dispatch({ type: "SET_RECORDING_LIST", value: asList(res.meet_recording_list) });
  dispatch({ type: "SET_VIEWED_LIST", value: asArray(res.viewed_list) });
  dispatch({ type: "SET_DOWNLOAD_LIST", value: asObject(res.get_downloads) });
  dispatch({ type: "SET_MODULE_INSPECT_LIST", value: asList(res.inspect_module_list) });
  dispatch({ type: "SET_REPLAY_INPECT_LIST", value: asList(res.inspect_replay_list) });
  dispatch({ type: "SET_MEET_INSPECT_LIST", value: asList(res.inspect_meeting_list) });
  dispatch({ type: "SET_USER_CHART_LIST", value: asObject(res.user_chart) });

  dispatch({ type: "LOADED_LOGIN_HIST_LIST", value: true });
  dispatch({ type: "LOADED_GOOGLE_INSPECT_LIST", value: true });
  dispatch({ type: "LOADED_REPLAY_INSPECT_LIST", value: true });
  dispatch({ type: "LOADED_MODULE_INSPECT_LIST", value: true });
};

/**
 * Applies a parsed unit payload to the app store.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {object} res Unit JSON.
 * @returns {void}
 */
const applyCourseUnitPayload = (dispatch, res) => {
  dispatch({ type: "SET_XBLOCK", value: asArray(res.xblock_list) });
  dispatch({ type: "SET_COMMENT_LIST", value: asList(res.comment_list) });
  dispatch({ type: "SET_REVIEW", value: asObject(res.review_get) });
  dispatch({ type: "SET_CURRENT_VIMEO_SEEK", value: asObject(res.inspect_vimeo_get) });
};

/**
 * Serializes a GraphQL field the same way live queries store it in IndexedDB.
 *
 * @param {*} value Field value.
 * @returns {string}
 */
const asCachedGql = (value) => (typeof value === "string" ? value : JSON.stringify(value ?? {}));

/**
 * Writes each unit payload using the same variables as `app_course_unit_get`.
 *
 * @param {string} courseId Course identifier.
 * @param {object} structure Course outline.
 * @param {object} unitMap Map of `${courseId}::i/j/k` to unit payloads.
 * @returns {void}
 */
const cacheCourseUnits = (courseId, structure, unitMap) => {
  ((structure && structure.sections) || []).forEach((section, sectionIdx) => {
    ((section && section.sequences) || []).forEach((sequence, sequenceIdx) => {
      ((sequence && sequence.verticals) || []).forEach((vertical, verticalIdx) => {
        const router_url = `${sectionIdx}/${sequenceIdx}/${verticalIdx}`;
        const payload = unitMap[`${courseId}::${router_url}`];
        if (!payload) return;
        const xblocks = vertical.xblock_ids || [];
        putGqlCache(Q_APP_COURSE_UNIT_GET, {
          input: {
            course_id: courseId,
            vertical_id: vertical.vertical_id,
            discussion_id: xblocks.find((v) => v.type === "discussion")?.definition_id,
            vimeo_id: xblocks.find((v) => v.type === "videoxblock" || v.type === "video")?.vimeo_id,
            definition_ids: xblocks.map((v) => v.definition_id),
            router_url,
          },
        }, asCachedGql(payload));
      });
    });
  });
};

/**
 * Fetches the learner dashboard and hydrates the app store.
 * Clears JWTs only on genuine authentication/authorization failures.
 *
 * @param {Function} dispatch App store dispatch.
 * @returns {void}
 */
export const actionAppDashboardGet = (dispatch) => {
  asterAxios(dispatch, Q_APP_DASHBOARD_GET, {}, "LOADED_APP_DASHBOARD")
    .then((res) => {
      res = safeParse(res);

      if (!res?.user_get?.id) throw Error("Token is expired or API is not success!");

      applyDashboardPayload(dispatch, res);
    })
    .catch((err) => {
      if (isNetworkError(err)) return;
      if (isAuthFailure(err)) {
        void clearSyncQueue();
        clearSessionAndRedirect();
        return;
      }
      if (process.env.NODE_ENV !== "production") {
        console.warn("[app dashboard] non-auth error; session preserved", err);
      }
    });
};

/**
 * Loads course outline, comments, sessions, and inspect data for a course.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {string} courseId Course identifier.
 * @returns {void}
 */
export const actionAppCourseDetailGet = (dispatch, courseId) => {
  const course_id = courseId;
  asterAxios(dispatch, Q_APP_COURSE_DETAIL_GET, { course_id }, "LOADED_APP_COURSE_DETAIL_GET").then((res) => {
    res = safeParse(res);

    applyCourseDetailPayload(dispatch, res);
  }).catch(() => {});
};

/**
 * Loads XBlocks, comments, review, and Vimeo seek for a course unit.
 * Also records the last visited route and viewed vertical.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, vertical_id: string, discussion_id?: string, vimeo_id?: string, definition_ids?: Array, router_url: string }} value Unit request payload.
 * @returns {void}
 */
export const actionAppCourseUnitGet = (dispatch, value) => {
  const course_id = value.course_id;
  dispatch({ type: "UPDATE_ROUTER_TRACK", value: { courseId: course_id, url: value.router_url } });
  dispatch({ type: "UPDATE_VIEWED", value: value.router_url });
  // Set loaded after payload apply so Classic never paints SCORM with empty sha
  // while asterAxios's finally would flip loaded=true first.
  dispatch({ type: "LOADED_APP_COURSE_UNIT_GET", value: false });
  asterAxios(dispatch, Q_APP_COURSE_UNIT_GET, { input: value })
    .then((res) => {
      applyCourseUnitPayload(dispatch, safeParse(res));
      dispatch({ type: "LOADED_APP_COURSE_UNIT_GET", value: true });
    })
    .catch(() => {
      dispatch({ type: "LOADED_APP_COURSE_UNIT_GET", value: true });
    });
};

/**
 * Fetches dashboard, all enrolled course outlines, and all unit xblocks in one
 * request. Used on first page load/refresh instead of the three original APIs.
 *
 * @param {Function} dispatch App store dispatch.
 * @returns {Promise<void>}
 */
export const actionAppFirstLoadGet = (dispatch) => {
  return asterAxios(dispatch, Q_APP_FIRST_LOAD_GET, {}, "LOADED_APP_DASHBOARD")
    .then((res) => {
      const bundle = safeParse(res) || {};
      const dashboard = safeParse(bundle.app_dashboard_get) || bundle.app_dashboard_get || {};
      const details = bundle.app_course_detail_get || {};
      const units = bundle.app_course_unit_get || {};

      if (!dashboard?.user_get?.id) throw Error("Token is expired or API is not success!");

      applyDashboardPayload(dispatch, dashboard);
      putGqlCache(Q_APP_DASHBOARD_GET, {}, asCachedGql(dashboard));

      Object.keys(details).forEach((courseId) => {
        const detail = safeParse(details[courseId]) || details[courseId] || {};
        putGqlCache(Q_APP_COURSE_DETAIL_GET, { course_id: courseId }, asCachedGql(detail));
        cacheCourseUnits(courseId, detail.course_structure_get, units);
      });

      const current = parseAppPath(readLastPath() || (typeof window !== "undefined" ? window.location.pathname : ""));
      markFirstLoadPrimed(null);

      if (current.courseId && details[current.courseId]) {
        const detail = safeParse(details[current.courseId]) || details[current.courseId] || {};
        applyCourseDetailPayload(dispatch, detail);
        dispatch({ type: "LOADED_APP_COURSE_DETAIL_GET", value: true });
        markFirstLoadPrimed(current.courseId);

        if (current.routerUrl) {
          dispatch({ type: "UPDATE_VIEWED", value: current.routerUrl });
          dispatch({ type: "UPDATE_ROUTER_TRACK", value: { courseId: current.courseId, url: current.routerUrl } });
          const unit = units[`${current.courseId}::${current.routerUrl}`];
          if (unit) {
            applyCourseUnitPayload(dispatch, safeParse(unit) || unit);
            dispatch({ type: "LOADED_APP_COURSE_UNIT_GET", value: true });
          }
        }
      }
    })
    .catch((err) => {
      if (isAuthFailure(err) || (err && String(err.message || "").includes("Token is expired"))) {
        void clearSyncQueue();
        clearSessionAndRedirect();
        return;
      }
      if (isNetworkError(err)) return;
      if (process.env.NODE_ENV !== "production") {
        console.warn("[app first load] non-auth error; session preserved", err);
      }
    });
};

/**
 * Sends a learner survey email.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {Array<{ question: string, answer: string }>} value Survey answers.
 * @returns {Promise<*>}
 */
export const actionSendSurveyEmail = (dispatch, value) => asterAxios(dispatch, Q_SEND_SURVEY_EMAIL, { input: value });

/**
 * Enables or disables course preview mode.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {*} value Preview payload.
 * @returns {void}
 */
export const actionPreviewModeSet = (dispatch, value) => dispatch({ type: "SET_PREVIEW_MODE", value });

/**
 * Fetches paginated login history for the current learner.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ page: number, page_size: number }} value Pagination.
 * @returns {void}
 */
export const actionLoginHistList = (dispatch, value) => {
  const page = value.page;
  const page_size = value.page_size;
  asterAxios(dispatch, Q_LOGIN_HISTORY_LIST, { ...value, page, page_size }, "LOADED_LOGIN_HIST_LIST").then((res) => dispatch({ type: "SET_LOGIN_HIST_LIST", value: asList(res) }));
};

/**
 * Updates the learner profile and optionally shows a success or error toast.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {Object} value Profile fields to persist.
 * @param {Function} t i18n translate function.
 * @param {boolean} [showMsg=true] Whether to show a toast.
 * @returns {void}
 */
export const actionEditAccount = (dispatch, value, t, showMsg = true) => {
  dispatch({ type: "UPDATE_USER", value });
  const data = Object.keys(value).map((key) => ({ name: key, value: convert2String(value[key]) }));
  asterAxios(dispatch, M_USER_EDIT, { input: data }, "SET_PROFILE_UPDATE_LOADING")
    .then((res) => {
      dispatch({ type: "SET_PROFILE_UPDATE_SUCCESS", value: res });
      showMsg && toaster({ type: "success", title: t("Profile edit success!") });
    })
    .catch(() => showMsg && toaster({ type: "error", title: t("Profile edit error!") }));
};

/**
 * Applies problem answers locally and posts them unless posting is disabled.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ payload_list: Array, course_id: string, section_title: string, post_flag?: string }} value Answer payload.
 * @returns {void}
 */
export const actionAnswerSubmit = (dispatch, value) => {
    /**
     * Writes submitted answers into the local course grade store.
     *
     * @returns {void}
     */
    const applyGrades = () => {
    for (let payload of value.payload_list) {
      dispatch({ type: "UPDATE_COURSE_GRADE", value: { grade: payload, course_id: value.course_id, section_title: value.section_title } });
    }
  };

  if (value.post_flag === "NOT_ALLOW_POST") {
    applyGrades();
    return;
  }

  const sendData = value.payload_list.map((p) => ({
    course_id: p.course_id,
    module_id: p.module_id,
    raw_possible: p.raw_possible,
    choices: p.choices,
  }));
  asterAxios(dispatch, M_STUDENT_ANSWER_EDIT, { input: sendData }, "LOADED_APP_DASHBOARD")
    .then(() => applyGrades())
    .catch(() => toaster({ type: "error", title: "Answer submit failed" }));
};

/**
 * Creates or updates a course/module certificate record.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, course_title: string, module_title: string, is_final?: boolean, total?: number, is_save?: boolean }} value Certificate payload.
 * @returns {void}
 */
export const actionCreateCertificate = (dispatch, value) => {
  const course_id = value.course_id;
  asterAxios(dispatch, M_CERTIFICATE_CREATE, { input: { ...value, course_id } })
    .then(() => {
      value.is_save && dispatch({ type: "ADD_CERTIFICATE", value });
      dispatch({ type: "UPDATE_CERTIFICATE", value });
    })
    .catch(() => toaster({ type: "error", title: "Certificate create failed" }));
};

/**
 * Fetches the first page of inbox messages.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ user_id: number, page: number, page_size: number }} value Chat list query.
 * @returns {void}
 */
export const actionChatList = (dispatch, value) => {
  const page = value.page;
  const page_size = value.page_size;
  const user_id = value.user_id;
  asterAxios(dispatch, Q_LIST_CHAT, { ...value, page, page_size, user_id }, "SET_CHAT_LIST_LOADED").then((res) => dispatch({ type: "SET_CHAT_LIST", value: asObject(res) }));
};

/**
 * Fetches the next page of inbox messages and appends them.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ user_id: number, page: number, page_size: number }} value Chat list query.
 * @returns {void}
 */
export const actionChatMoreList = (dispatch, value) => {
  const page = value.page;
  const page_size = value.page_size;
  const user_id = value.user_id;
  asterAxios(dispatch, Q_LIST_CHAT, { ...value, page, page_size, user_id }, "SET_CHAT_MORE_LIST_LOADED").then((res) => dispatch({ type: "SET_CHAT_MORE_LIST", value: asObject(res) }));
};

/**
 * Optimistically adds a chat message and posts it unless it is a local-only draft.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ user_id: number, text?: string, urls?: Array, is_new?: boolean }} value Chat message.
 * @returns {void}
 */
export const actionChatAdd = (dispatch, value) => {
  dispatch({ type: "ADD_CHAT", value });

  const user_id = value.user_id;
  const data = {
    event_id: `${process.env.REACT_APP_SITE_NAME}_${user_id}`,
    text: value.text || "",
    urls: value.urls || [],
    created: value.created || new Date().toISOString(),
  };
  if (!value.is_new) asterAxios(dispatch, M_ADD_CHAT, data);
};

/**
 * Marks every inbox message as read in the local store.
 *
 * @param {Function} dispatch App store dispatch.
 * @returns {void}
 */
export const actionChatMarkAllRead = (dispatch) => {
  dispatch({ type: "CHAT_MARK_ALL_READ" });
};

/**
 * Broadcast a typing indicator for the current chat room.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {string} eventId Chat room event id.
 * @param {boolean} typing Whether the user is typing.
 * @returns {void}
 */
export const actionChatTyping = (dispatch, eventId, typing) => {
  if (!eventId) return;
  asterAxios(dispatch, M_CHAT_TYPING, { event_id: eventId, typing: Boolean(typing) }).catch(() => {});
};

/**
 * Mark the current user online. Skipped while the client is offline so pings
 * are not queued and the other side does not keep a stale green status.
 *
 * @returns {void}
 */
export const actionPresencePing = () => {
  if (!isOnline()) return;
  postGql(M_PRESENCE_PING, {}).catch(() => {});
};

/**
 * Mark the current user offline. Always hits the network (including simulated
 * offline) so My Network can flip the green dot immediately.
 *
 * @returns {void}
 */
export const actionPresenceLeave = () => {
  postGqlKeepalive(M_PRESENCE_LEAVE, {}).catch(() => {});
};

/**
 * Load classmates and staff for My Network.
 *
 * @param {Function} dispatch App store dispatch.
 * @returns {void}
 */
export const actionNetworkList = (dispatch) => {
  asterAxios(dispatch, Q_NETWORK_LIST, {}, "LOADED_NETWORK_LIST")
    .then((res) => {
      dispatch({ type: "SET_NETWORK_LIST", value: asArray(res) });
    })
    .catch(() => {});
};

/**
 * Fetch a peer-to-peer thread.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ peer_id: number, page?: number, page_size?: number }} value Query.
 * @returns {void}
 */
export const actionPeerChatList = (dispatch, value) => {
  const peer_id = value.peer_id;
  const page = value.page || 0;
  const page_size = value.page_size || 20;
  asterAxios(dispatch, Q_LIST_CHAT, { peer_id, page, page_size }, "SET_PEER_CHAT_LOADED")
    .then((res) => {
      dispatch({ type: "SET_PEER_CHAT", value: { ...asObject(res), peerId: peer_id } });
    })
    .catch(() => {});
};

/**
 * Optimistically add a peer message and persist unless it is a Pusher echo.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ user_id: number, peer_id: number, text?: string, urls?: Array, is_new?: boolean, from_id?: number }} value Message.
 * @returns {void}
 */
export const actionPeerChatAdd = (dispatch, value) => {
  dispatch({ type: "ADD_PEER_CHAT", value });
  if (value.is_new) return;
  const data = {
    event_id: `${process.env.REACT_APP_SITE_NAME}_${value.user_id}_${value.peer_id}`,
    text: value.text || "",
    urls: value.urls || [],
    created: value.created || new Date().toISOString(),
  };
  asterAxios(dispatch, M_ADD_CHAT, data);
};

/**
 * Fetches paginated discussion comments.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ user_id?: number, page: number, page_size: number }} value Comment list query.
 * @returns {void}
 */
export const actionCommentList = (dispatch, value) => {
  const page = value.page;
  const page_size = value.page_size;
  asterAxios(dispatch, Q_COMMENT_LIST, { ...value, page, page_size }, "LOADED_COMMENT_LIST").then((res) => dispatch({ type: "SET_COMMENT_LIST", value: asList(res) }));
};

/**
 * Creates a discussion comment and appends it to the store.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {Object} value Comment payload including course and body fields.
 * @returns {void}
 */
export const actionCreateComment = (dispatch, value) => {
  asterAxios(dispatch, M_COMMENT_CREATE, { input: value }).then((res) => dispatch({ type: "ADD_COMMENT", value: { _id: res, ...value } }));
};

/**
 * Updates a discussion comment body, files, or visibility.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ comment_id: string, body?: string, files?: Array, visible?: boolean }} value Comment edit payload.
 * @returns {void}
 */
export const actionEditComment = (dispatch, value) => {
  asterAxios(dispatch, M_COMMENT_EDIT, { input: value })
    .then(() => dispatch({ type: "EDIT_COMMENT", value }))
    .catch(() => toaster({ type: "error", title: "Comment edit failed" }));
};

/**
 * Sends an email to the learner's coach.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ email: string, name: string, html: string }} value Email payload.
 * @returns {Promise<*>}
 */
export const actionSendCoachEmail = (dispatch, value) => {
  const email = value.email;
  return asterAxios(dispatch, Q_SEND_COACH_EMAIL, { ...value, email });
};

/**
 * Sets whether the incomplete-profile alert is shown.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {boolean} flag Alert visibility.
 * @returns {void}
 */
export const actionSetIsProfileAlert = (dispatch, flag) => dispatch({ type: "SET_IS_PROFILE_ALERT", value: flag });

/**
 * Fetches paginated module time-on-task inspect records.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, page: number, page_size: number }} value List query.
 * @returns {void}
 */
export const actionModuleInspectList = (dispatch, value) => {
  const course_id = value.course_id;
  const page = value.page;
  const page_size = value.page_size;
  asterAxios(dispatch, Q_MODULE_INSPECT_LIST, { ...value, course_id, page, page_size }, "LOADED_MODULE_INSPECT_LIST").then((res) => dispatch({ type: "SET_MODULE_INSPECT_LIST", value: asList(res) }));
};

/**
 * Fetches paginated past live-session recordings.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, page: number, page_size: number }} value List query.
 * @returns {void}
 */
export const actionPastSessionList = (dispatch, value) => {
  const course_id = value.course_id;
  const page = value.page;
  const page_size = value.page_size;
  asterAxios(dispatch, Q_PAST_SESSION_LIST, { ...value, course_id, page, page_size }, "SET_PAST_SESSION_LOADED").then((res) => dispatch({ type: "SET_RECORDING_LIST", value: asList(res) }));
};

/**
 * Records module time-on-task locally and persists it. Ghost JWTs disable tracking on failure.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, hash?: string, module_title: string, total_spent: number, quiz_spent: number, correct_count?: number, total_count?: number, progress?: number }} value Inspect payload.
 * @returns {Promise<*>}
 */
export const actionModuleInspectEdit = (dispatch, value) => {
  if (localStorage.getItem("jwt-ghost")) return Promise.resolve();

  const course_id = value.course_id;
  dispatch({ type: "UPDATE_MODULE_INSPECT", value });
  return asterAxios(dispatch, M_MODULE_INSPECT_EDIT, { input: [{ ...value, course_id }] }).catch(() => {});
};

/**
 * Fetches paginated Google Meet inspect records.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, page: number, page_size: number }} value List query.
 * @returns {void}
 */
export const actionGoogleInspectList = (dispatch, value) => {
  const course_id = value.course_id;
  const page = value.page;
  const page_size = value.page_size;
  asterAxios(dispatch, Q_GOOGLE_INSPECT_LIST, { ...value, course_id, page, page_size }, "LOADED_GOOGLE_INSPECT_LIST").then((res) => dispatch({ type: "SET_MEET_INSPECT_LIST", value: asList(res) }));
};

/**
 * Fetches paginated replay inspect records.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, page: number, page_size: number }} value List query.
 * @returns {void}
 */
export const actionReplayInspectList = (dispatch, value) => {
  const course_id = value.course_id;
  const page = value.page;
  const page_size = value.page_size;
  asterAxios(dispatch, Q_REPLAY_INSPECT_LIST, { ...value, course_id, page, page_size }, "LOADED_REPLAY_INSPECT_LIST").then((res) => dispatch({ type: "SET_REPLAY_INPECT_LIST", value: asList(res) }));
};

/**
 * Records replay watch time locally and persists it.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, hash?: string, zoom_id?: string, vimeo_id?: string, topic?: string, spent: number, instructor_name?: string }} value Inspect payload.
 * @returns {void}
 */
export const actionReplayInspectEdit = (dispatch, value) => {
  const course_id = value.course_id;
  dispatch({ type: "UPDATE_REPLAY_INSPECT", value });
  asterAxios(dispatch, M_REPLAY_INSPECT_EDIT, { input: { ...value, course_id } }).catch(() => {});
};

/**
 * Fetches learner activity chart points for a date range.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ start: string|number, end: string|number }} value Date range.
 * @returns {void}
 */
export const actionUserChartList = (dispatch, value) => {
  const start = value.start;
  const end = value.end;
  asterAxios(dispatch, Q_LIST_USER_CHART, { ...value, start, end }, "SET_USER_CHART_LOADED").then((res) => dispatch({ type: "SET_USER_CHART_LIST", value: asObject(res) }));
};

/**
 * Saves a unit review locally and persists it.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, vertical_id: string, type: string, content: string }} value Review payload.
 * @returns {void}
 */
export const actionEditReview = (dispatch, value) => {
  const course_id = value.course_id;
  dispatch({ type: "SET_REVIEW", value });
  asterAxios(dispatch, M_REVIEW_EDIT, { input: { ...value, course_id } }).catch(() => {});
};

/**
 * Persists Vimeo playback progress for a unit.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {{ course_id: string, vimeo_id: string, module_title?: string, vimeo_title?: string, seek: number, duration: number, spent: number, hash?: string }} value Inspect payload.
 * @returns {Promise<*>}
 */
export const actionVimeoInspectEdit = (dispatch, value) => {
  const course_id = value.course_id;
  return asterAxios(dispatch, M_VIMEO_INSPECT_EDIT, { input: { ...value, course_id } }).catch(() => {});
};
